import { useEffect, useId, useRef, useState } from 'react'
import type { SiteIssue, TamperedCamera } from '../siteIssues'
import { tamperLabel } from '../tamperSignal'
import { relTime } from '../relTime'

interface Props {
  issues: SiteIssue[]
  onSelectSite: (siteId: string) => void
  /** Opens the incident behind a tamper row. Absent rows offer no control. */
  onOpenIncident?: (clusterId: number) => void
  /**
   * Accepts a camera's current view as the correct one.
   *
   * Not a dismiss: a camera that has been legitimately re-aimed reports
   * tampering forever, because nothing can tell a deliberate move from an
   * interfering one and the reference still describes the old view. This says
   * what the view is supposed to be; the report clears because the comparison
   * then passes.
   */
  onAcceptView?: (siteId: string, camera: string) => void
  /** The picture of an incident, for a tampered camera's row. */
  pictureOf?: (clusterId: number) => string | undefined
}

// Past this a row wraps over the whole panel and pushes every other site out
// of view. The count above it stays exact; only the names stop.
const maxNamed = 6

// Amber wherever the subject is tampering or degradation, red wherever it is
// an outage. Colouring a recording camera like a dead one teaches people to
// discount the red.
function tone(issue: SiteIssue): string {
  if (issue.kind === 'cameras-tampered') return 'text-attention'
  return issue.kind === 'cameras-degraded' ? 'text-degraded' : 'text-fault'
}

// The noun is dropped only when two figures share the line: "2 cameras
// offline, 2 degraded" reads as one list of four rather than two counts.
function describe(issue: SiteIssue): string {
  if (issue.kind === 'cameras-tampered') {
    const n = issue.cameras.length
    // The outage still has to be said: ranking decides the kind, not what the
    // row may report, and a site that is both is in more trouble, not less.
    return `${n} ${n === 1 ? 'camera' : 'cameras'} tampered${issue.alsoUnreachable ? ' · unreachable' : ''}`
  }
  if (issue.kind === 'unreachable') return 'Unreachable'
  if (issue.kind === 'all-cameras-offline') return 'All cameras offline'
  const n = issue.cameras.length
  if (issue.degraded?.length) return `${n} offline · ${issue.degraded.length} degraded`
  const noun = n === 1 ? 'camera' : 'cameras'
  return `${n} ${noun} ${issue.kind === 'cameras-degraded' ? 'degraded' : 'offline'}`
}

// The two whole-site kinds point at different fixes, which their names alone
// left to be inferred.
const meaning: Partial<Record<SiteIssue['kind'], string>> = {
  'unreachable': 'The site isn’t answering, so its cameras can’t be checked.',
  'all-cameras-offline': 'The site answers, but no camera is sending video.',
}

const icons: Record<SiteIssue['kind'], string> = {
  'cameras-tampered': 'M9.9 4.2A10 10 0 0 1 12 4c7 0 10 8 10 8a17 17 0 0 1-2.2 3.3M6.6 6.6A17 17 0 0 0 2 12s3 8 10 8a9.7 9.7 0 0 0 5.4-1.6M2 2l20 20M9.9 9.9a3 3 0 0 0 4.2 4.2',
  'unreachable': 'M12 20h.01M8.5 16.4a5 5 0 0 1 7 0M5 12.9a10 10 0 0 1 5.2-2.7M19 12.9a10 10 0 0 0-2-1.4M2 8.8a15 15 0 0 1 4.2-2.7M22 8.8A15 15 0 0 0 11 5M2 2l20 20',
  'all-cameras-offline': 'M10.7 5H14a2 2 0 0 1 2 2v3.3l1 1L22 7v10M16 16a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h1M2 2l20 20',
  'cameras-offline': 'M10.7 5H14a2 2 0 0 1 2 2v3.3l1 1L22 7v10M16 16a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h1M2 2l20 20',
  'cameras-degraded': 'M15 10l4.5-2.3A1 1 0 0 1 21 8.6v6.8a1 1 0 0 1-1.5.9L15 14M5 18h8a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2zM9 10v3M9 15.5h.01',
}

function chipsOf(issue: SiteIssue): { name: string; state: 'offline' | 'degraded' }[] {
  if (issue.kind === 'cameras-offline') {
    return [...issue.cameras.map((name) => ({ name, state: 'offline' as const })), ...(issue.degraded ?? []).map((name) => ({ name, state: 'degraded' as const }))]
  }
  if (issue.kind === 'cameras-degraded') return issue.cameras.map((name) => ({ name, state: 'degraded' as const }))
  return []
}

/**
 * The header's count of sites currently worth attention, and what they are.
 *
 * Counts sites rather than cameras: one site losing forty cameras is one thing
 * to go and fix, and a badge reading "40" presents it as forty faults. Each row
 * names the cameras underneath for the same reason — the detail is there without
 * the list being made of it.
 */
export function IssuesIndicator({ issues, onSelectSite, onOpenIncident, onAcceptView, pictureOf }: Props) {
  const [open, setOpen] = useState(false)
  // The camera whose view is being accepted, which asks first; and the one to
  // hand focus back to when that is cancelled.
  const [confirming, setConfirming] = useState<string | null>(null)
  const [returnTo, setReturnTo] = useState<string | null>(null)
  const close = () => {
    setOpen(false)
    setConfirming(null)
    setReturnTo(null)
  }
  const ref = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)

  // Nothing to show means nothing to open: an empty panel reads as a failure to
  // load rather than as good news.
  const canOpen = issues.length > 0
  const showing = open && canOpen

  useEffect(() => {
    if (!showing) return
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) {
        setOpen(false)
        setConfirming(null)
      }
    }
    const onEsc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // Escape backs out of the question first, then the list.
      if (confirming) {
        setReturnTo(confirming)
        setConfirming(null)
      } else {
        setOpen(false)
        trigger.current?.focus()
      }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onEsc)
    }
  }, [showing, confirming])

  // Said in words: a bare icon and a number left the number to be guessed at.
  const words = issues.length === 1 ? 'site needs attention' : 'sites need attention'

  return (
        <div className="relative" ref={ref}>
          <button
            ref={trigger}
            onClick={() => (showing ? close() : setOpen(true))}
            aria-expanded={showing}
            aria-label={canOpen ? `${issues.length} ${words}` : 'No site issues'}
            className={`h-9 px-3 flex items-center gap-2 rounded-full border text-sm font-semibold transition-colors ${
              canOpen
                ? 'border-fault/40 bg-fault/10 text-fault hover:bg-fault/15'
                : 'border-zinc-800 text-zinc-400 hover:bg-zinc-800'
            }`}
          >
            {/* Healthy is stated rather than merely unlit: a dimmed warning, or no
                icon at all, reads the same as an indicator that failed to load or
                stopped polling. A different shape carries it for anyone who cannot
                rely on the colour. */}
            <svg
              data-testid={canOpen ? 'issues-warning' : 'issues-healthy'}
              aria-hidden="true"
              className={`w-4 h-4 ${canOpen ? '' : 'text-live'}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d={
                  canOpen
                    ? 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z'
                    : 'M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                }
              />
            </svg>
            {canOpen ? (
              <span>
                <span data-testid="issue-count" className="tabular-nums">{issues.length}</span>
                <span className="hidden sm:inline"> {words}</span>
              </span>
            ) : (
              <span className="hidden sm:inline">No site issues</span>
            )}
          </button>

          {showing && (
            <section
              aria-labelledby="issues-title"
              className="absolute right-0 mt-2 w-[26rem] max-w-[calc(100vw-2rem)] max-sm:fixed max-sm:inset-x-4 max-sm:top-16 max-sm:mt-0 max-sm:w-auto max-sm:max-w-none max-h-[min(40rem,calc(100dvh-5rem))] overflow-y-auto scrollbar-thin rounded-lg border border-zinc-800 bg-zinc-900 shadow-[0_24px_48px_rgb(0_0_0/0.55)] z-50"
            >
              {/* Sticky: a list long enough to scroll would otherwise lose the
                  label that says what it is. */}
              <div className="sticky top-0 z-10 px-4 pt-3.5 pb-2.5 border-b border-zinc-800 bg-zinc-900 flex items-baseline justify-between gap-3">
                <h2 id="issues-title" className="text-base font-semibold text-zinc-100">Site issues</h2>
                <span className="text-[13px] text-zinc-400">Most urgent first</span>
              </div>
              <ul role="list" className="p-2 space-y-2">
                {issues.map((issue) => {
                  const chips = chipsOf(issue)
                  const isTamper = issue.kind === 'cameras-tampered'
                  return (
                    <li
                      key={issue.siteId}
                      className={`rounded-md border overflow-hidden ${isTamper ? 'border-attention/55 bg-attention/[0.06]' : 'border-zinc-800'}`}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          onSelectSite(issue.siteId)
                          close()
                        }}
                        className="w-full px-3 py-2.5 flex items-start gap-3 text-left hover:bg-zinc-800/60 transition-colors"
                      >
                        <span aria-hidden="true" className={`shrink-0 w-[30px] h-[30px] rounded-full flex items-center justify-center ${isTamper ? 'bg-attention/10' : 'bg-fault/10'} ${tone(issue)}`}>
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                            <path d={icons[issue.kind]} />
                          </svg>
                        </span>
                        <span className="flex-1 min-w-0 flex flex-col gap-0.5">
                          <span className="text-sm font-semibold text-zinc-100 [overflow-wrap:anywhere]">{issue.siteName}</span>
                          <span className={`text-[13px] font-semibold ${tone(issue)}`}>{describe(issue)}</span>
                          {meaning[issue.kind] && <span className="text-[13px] text-zinc-400">{meaning[issue.kind]}</span>}
                        </span>
                        <span className="shrink-0 self-center flex items-center gap-1 text-[13px] text-zinc-400">
                          Go to site
                          <svg aria-hidden="true" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
                        </span>
                      </button>
                      {chips.length > 0 && (
                        <div className="pl-[54px] pr-3 pb-3 flex flex-wrap gap-1.5">
                          {chips.slice(0, maxNamed).map((c) => (
                            <span key={`${c.state}|${c.name}`} className={`h-[26px] px-2 flex items-center gap-1.5 rounded border text-[13px] text-zinc-100 whitespace-nowrap ${c.state === 'offline' ? 'border-fault/40' : 'border-zinc-700'}`}>
                              <span aria-hidden="true" className={`w-[7px] h-[7px] rounded-full ${c.state === 'offline' ? 'bg-fault' : 'bg-degraded'}`} />
                              <span>{c.name}</span>
                              <span className="text-zinc-400">{c.state}</span>
                            </span>
                          ))}
                          {chips.length > maxNamed && (
                            <span className="h-[26px] px-1 flex items-center text-[13px] text-zinc-400">+{chips.length - maxNamed} more</span>
                          )}
                        </div>
                      )}
                      {isTamper && (issue.tampered ?? []).map((t) => {
                        const key = `${issue.siteId}\u0001${t.camera}`
                        return (
                          <TamperedRow
                            key={key}
                            camera={t}
                            picture={t.clusterId !== undefined ? pictureOf?.(t.clusterId) : undefined}
                            confirming={confirming === key}
                            refocus={returnTo === key}
                            onOpenIncident={onOpenIncident && t.clusterId !== undefined ? () => {
                              onOpenIncident(t.clusterId!)
                              close()
                            } : undefined}
                            onAsk={onAcceptView ? () => setConfirming(key) : undefined}
                            onCancel={() => {
                              setReturnTo(key)
                              setConfirming(null)
                            }}
                            onAccept={() => {
                              onAcceptView?.(issue.siteId, t.camera)
                              close()
                            }}
                          />
                        )
                      })}
                    </li>
                  )
                })}
              </ul>
            </section>
          )}
    </div>
  )
}

// TamperedRow is one tampered camera under its site: what was lost and since
// when, as its tile says it, and what can be done about it. Opening the
// incident is the first move and looks like it. Accepting the view is the one
// action that can hide a real tamper, so it stays quiet and asks first.
function TamperedRow({ camera: t, picture, confirming, refocus, onOpenIncident, onAsk, onCancel, onAccept }: {
  camera: TamperedCamera
  picture?: string
  confirming: boolean
  refocus: boolean
  onOpenIncident?: () => void
  onAsk?: () => void
  onCancel: () => void
  onAccept: () => void
}) {
  const questionId = useId()
  return (
    <div role="group" aria-label={t.camera} className="mx-3 mb-3 ml-[54px] p-2.5 flex flex-col gap-2.5 rounded border border-zinc-800 bg-zinc-950">
      <div className="flex items-center gap-2.5">
        {picture && <img src={picture} alt={`Picture from ${t.camera}`} className="w-16 h-9 shrink-0 rounded object-cover bg-zinc-800" />}
        <span className="min-w-0 flex flex-col gap-px">
          <span className="text-sm font-semibold text-zinc-100 [overflow-wrap:anywhere]">{t.camera}</span>
          <span className="text-[13px] text-zinc-400">
            <span className="text-attention">{tamperLabel(t)}</span> · {relTime(Date.parse(t.since) / 1000)}
          </span>
        </span>
      </div>
      {confirming ? (
        <div role="alertdialog" aria-labelledby={questionId} className="flex flex-col gap-2 pt-2 border-t border-zinc-800">
          <span id={questionId} className="text-sm font-semibold text-zinc-100">Accept this as the camera’s view?</span>
          <span className="text-[13px] text-zinc-400">
            Do this only if the camera was re-aimed on purpose. Tampering is judged against this picture from now on, and the change is recorded under your name.
          </span>
          <span className="flex justify-end gap-2">
            <button type="button" autoFocus onClick={onCancel} className="h-[34px] px-3 rounded-md border border-zinc-700 bg-zinc-800 text-sm font-semibold text-zinc-100 hover:bg-zinc-700">Cancel</button>
            <button type="button" onClick={onAccept} className="h-[34px] px-3 rounded-md border border-attention/55 text-sm font-semibold text-attention hover:bg-attention/10">Accept view</button>
          </span>
        </div>
      ) : (onOpenIncident || onAsk) && (
        <span className="flex flex-wrap gap-2">
          {onOpenIncident && (
            <button type="button" onClick={onOpenIncident} className="h-[34px] px-3 rounded-md bg-attention text-sm font-bold text-zinc-950 hover:brightness-110">Open incident</button>
          )}
          {onAsk && (
            <button type="button" autoFocus={refocus} onClick={onAsk} className="h-[34px] px-3 rounded-md border border-zinc-700 text-sm font-semibold text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800">Accept this view…</button>
          )}
        </span>
      )}
    </div>
  )
}
